import { createTheme } from '@mui/material/styles';

// Brand colors from tailwind.config.js / clinicui reference
const brand = {
  orange: '#F28E2E',
  teal: '#3A7380',
  darkTeal: '#284C58',
  mint: '#B8DDD0',
  modalBg: '#B4D9D4',
  modalText: '#5B7F8A',
  titleText: '#366A75',
  fieldBg: '#EBF5F5',
  buttonHover: '#D0E7E5',
  closeBtnHover: '#2D5A60',
};

const theme = createTheme({
  direction: 'rtl',
  palette: {
    primary: {
      main: brand.teal,        // #3A7380 — clinicui brand teal
      dark: brand.darkTeal,    // #284C58
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brand.orange,      // #F28E2E — clinicui brand orange
      contrastText: '#FFFFFF',
    },
    success: { main: '#10B981' },
    error: { main: '#EF4444' },
    warning: { main: '#F59E0B' },
    info: { main: '#06B6D4' },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280',
      disabled: '#9CA3AF',
    },
    background: {
      default: '#F9FAFB',
      paper: '#FFFFFF',
    },
    divider: '#E5E7EB',
    action: {
      hover: 'rgba(58, 115, 128, 0.04)',    // subtle teal tint on hover
      selected: 'rgba(58, 115, 128, 0.08)',
    },
  },

  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    button: {
      textTransform: 'none' as const,
      fontWeight: 500,
    },
  },

  shape: {
    borderRadius: 8, // clinicui default 0.5rem
  },

  components: {
    /* ── CssBaseline ─────────────────────────────── */
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F9FAFB',
        },
      },
    },

    /* ── Buttons ─────────────────────────────────── */
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          textTransform: 'none' as const,
          padding: '8px 16px',
        },
        containedPrimary: {
          backgroundColor: brand.teal,
          '&:hover': { backgroundColor: brand.darkTeal },
        },
        containedSecondary: {
          backgroundColor: brand.orange,
          '&:hover': { backgroundColor: '#D97A1F' },
        },
        outlinedPrimary: {
          borderColor: brand.teal,
          color: brand.teal,
          '&:hover': {
            backgroundColor: 'rgba(58, 115, 128, 0.04)',
            borderColor: brand.darkTeal,
          },
        },
      },
    },

    /* ── IconButton ──────────────────────────────── */
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(58, 115, 128, 0.08)',
          },
        },
      },
    },

    /* ── Text fields / inputs ────────────────────── */
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.teal,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.teal,
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.teal,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.teal,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: brand.teal,
          },
        },
      },
    },

    /* ── Select ──────────────────────────────────── */
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    /* ── Chip / Badge ────────────────────────────── */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        colorPrimary: {
          backgroundColor: 'rgba(58, 115, 128, 0.1)',
          color: brand.teal,
        },
        colorSecondary: {
          backgroundColor: 'rgba(242, 142, 46, 0.1)',
          color: brand.orange,
        },
      },
    },

    /* ── Paper / Card ────────────────────────────── */
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none', // remove MUI's default gradient overlay
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: '1px solid #E5E7EB',
          borderRadius: 12,
        },
      },
    },

    /* ── Table ───────────────────────────────────── */
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#E5E7EB',
          padding: '12px 16px',
          fontSize: '0.875rem',
        },
        head: {
          backgroundColor: '#F9FAFB',
          color: '#374151',
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.05em',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F9FAFB',
          },
        },
      },
    },

    /* ── Dialog / Modal ──────────────────────────── */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#1F2937',
        },
      },
    },

    /* ── Alert ───────────────────────────────────── */
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: '#ECFDF5',
          color: '#065F46',
          border: '1px solid #A7F3D0',
        },
        standardError: {
          backgroundColor: '#FEF2F2',
          color: '#991B1B',
          border: '1px solid #FECACA',
        },
        standardWarning: {
          backgroundColor: '#FFFBEB',
          color: '#92400E',
          border: '1px solid #FDE68A',
        },
        standardInfo: {
          backgroundColor: '#ECFEFF',
          color: '#155E75',
          border: '1px solid #A5F3FC',
        },
      },
    },

    /* ── Tabs ────────────────────────────────────── */
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          '&.Mui-selected': {
            color: brand.teal,
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: brand.teal,
        },
      },
    },

    /* ── Tooltip ─────────────────────────────────── */
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1F2937',
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },

    /* ── Autocomplete ────────────────────────────── */
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        },
      },
    },

    /* ── Menu ────────────────────────────────────── */
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
          border: '1px solid #E5E7EB',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&:hover': {
            backgroundColor: '#F9FAFB',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(58, 115, 128, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(58, 115, 128, 0.12)',
            },
          },
        },
      },
    },

    /* ── Switch ──────────────────────────────────── */
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: brand.teal,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: brand.teal,
          },
        },
      },
    },

    /* ── Checkbox / Radio ────────────────────────── */
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: brand.teal,
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: brand.teal,
          },
        },
      },
    },

    /* ── CircularProgress ────────────────────────── */
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: brand.teal,
        },
      },
    },

    /* ── LinearProgress ──────────────────────────── */
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: 'rgba(58, 115, 128, 0.1)',
        },
        barColorPrimary: {
          backgroundColor: brand.teal,
        },
      },
    },

    /* ── TablePagination ─────────────────────────── */
    MuiTablePagination: {
      styleOverrides: {
        actions: {
          direction: 'ltr',
          '& button svg': {
            transform: 'scaleX(-1)',
          },
        },
      },
    },

    /* ── Pagination ──────────────────────────────── */
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: brand.teal,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: brand.darkTeal,
            },
          },
        },
      },
    },

    /* ── Fab ─────────────────────────────────────── */
    MuiFab: {
      styleOverrides: {
        primary: {
          backgroundColor: brand.teal,
          '&:hover': { backgroundColor: brand.darkTeal },
        },
      },
    },
  },
});

export default theme;
